import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Index from "./pages/Index";
import { prefetchIdleRoutes } from "./utils/prefetchRoute";
import { AuthProvider } from "./contexts/AuthContext";

// Defer non-critical imports from the initial JS bundle
const TooltipProvider = lazy(() =>
  import("@/components/ui/tooltip").then(m => ({ default: m.TooltipProvider }))
);

// Lazy-load non-critical UI that isn't needed for first paint
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));

// Lazy-loaded routes (everything except the home page)
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Intranet = lazy(() => import("./pages/Intranet"));
const FacaParte = lazy(() => import("./pages/FacaParte"));
const ANucleo = lazy(() => import("./pages/ANucleo"));
const ReducaoTributaria = lazy(() => import("./pages/solucoes/ReducaoTributaria"));
const CIT = lazy(() => import("./pages/CIT"));
const Academy = lazy(() => import("./pages/Academy"));
const AssessoriaContabil = lazy(() => import("./pages/solucoes/AssessoriaContabil"));
const ConsultoriaContabil = lazy(() => import("./pages/solucoes/ConsultoriaContabil"));
const BPOFinanceiro = lazy(() => import("./pages/solucoes/BPOFinanceiro"));
const AssessoriaBDMG = lazy(() => import("./pages/solucoes/AssessoriaBDMG"));
const CertificadoDigital = lazy(() => import("./pages/solucoes/CertificadoDigital"));
const ContPay = lazy(() => import("./pages/solucoes/ContPay"));
const Energia = lazy(() => import("./pages/solucoes/Energia"));
const TermosDeUso = lazy(() => import("./pages/TermosDeUso"));
const PoliticaDePrivacidade = lazy(() => import("./pages/PoliticaDePrivacidade"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Admin pages
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminPostForm = lazy(() => import("./pages/admin/AdminPostForm"));
const ProtectedRoute = lazy(() => import("./components/admin/ProtectedRoute").then(m => ({ default: m.ProtectedRoute })));

const LazyFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

/** Deferred ScrollToTop — loaded after first paint */
const DeferredScrollToTop = lazy(() =>
  import("./components/ScrollToTop").then(m => ({ default: m.ScrollToTop }))
);

/** Deferred Lenis smooth scroll — loaded only after first user interaction */
const useDeferredLenis = () => {
  useEffect(() => {
    let lenis: any;
    let rafId: number;
    let cleaned = false;

    const init = () => {
      if (cleaned) return;
      import('lenis').then(({ default: Lenis }) => {
        if (cleaned) return;
        lenis = new Lenis({
          duration: 1.2,
          easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
          orientation: 'vertical',
          gestureOrientation: 'vertical',
          smoothWheel: true,
        });
        (window as any).__lenis = lenis;
        function raf(time: number) {
          lenis.raf(time);
          rafId = requestAnimationFrame(raf);
        }
        rafId = requestAnimationFrame(raf);
      });
    };

    // Only fetch + init lenis after the user's first interaction
    const events = ['scroll', 'click', 'touchstart', 'keydown'] as const;
    const onInteraction = () => {
      events.forEach(e => window.removeEventListener(e, onInteraction));
      // Still defer to idle so it doesn't block the interaction itself
      if ('requestIdleCallback' in window) {
        requestIdleCallback(init, { timeout: 3000 });
      } else {
        setTimeout(init, 200);
      }
    };
    events.forEach(e => window.addEventListener(e, onInteraction, { once: true, passive: true }));

    return () => {
      cleaned = true;
      events.forEach(e => window.removeEventListener(e, onInteraction));
      if (rafId) cancelAnimationFrame(rafId);
      if (lenis) { delete (window as any).__lenis; lenis.destroy(); }
    };
  }, []);
};

const AppContent = () => {
  useDeferredLenis();

  // Prefetch main routes when browser is idle
  useEffect(() => {
    prefetchIdleRoutes();
  }, []);

  return (
    <AuthProvider>
    <BrowserRouter>
      <Suspense fallback={null}>
        <DeferredScrollToTop />
        <Toaster />
        <Sonner />
      </Suspense>
      <Suspense fallback={<LazyFallback />}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/intranet" element={<Intranet />} />
          <Route path="/faca-parte" element={<FacaParte />} />
          <Route path="/a-nucleo" element={<ANucleo />} />
          <Route path="/solucoes/reducao-tributaria" element={<ReducaoTributaria />} />
          <Route path="/cit" element={<CIT />} />
          <Route path="/academy" element={<Academy />} />
          <Route path="/solucoes/assessoria-contabil" element={<AssessoriaContabil />} />
          <Route path="/solucoes/consultoria-contabil" element={<ConsultoriaContabil />} />
          <Route path="/solucoes/bpo-financeiro" element={<BPOFinanceiro />} />
          <Route path="/solucoes/assessoria-bdmg" element={<AssessoriaBDMG />} />
          <Route path="/solucoes/certificado-digital" element={<CertificadoDigital />} />
          <Route path="/solucoes/contpay" element={<ContPay />} />
          <Route path="/solucoes/energia" element={<Energia />} />
          <Route path="/termos-de-uso" element={<TermosDeUso />} />
          <Route path="/politica-de-privacidade" element={<PoliticaDePrivacidade />} />
          {/* Admin routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<Suspense fallback={<LazyFallback />}><ProtectedRoute><AdminDashboard /></ProtectedRoute></Suspense>} />
          <Route path="/admin/posts/new" element={<Suspense fallback={<LazyFallback />}><ProtectedRoute><AdminPostForm /></ProtectedRoute></Suspense>} />
          <Route path="/admin/posts/:id/edit" element={<Suspense fallback={<LazyFallback />}><ProtectedRoute><AdminPostForm /></ProtectedRoute></Suspense>} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
    </AuthProvider>
  );
};

const App = () => (
  <Suspense fallback={null}>
    <TooltipProvider>
      <AppContent />
    </TooltipProvider>
  </Suspense>
);

export default App;
